import { createFileRoute, redirect, useRouter, useSearch } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import type { RegulatoryDocumentRequestsQueryQuery } from "@repo/graphql-types/graphql";
import Left from "@repo/icons/default/Left";
import Trash from "@repo/icons/default/Trash";
import { CpsBadge, type CpsBadgeProps } from "corpus";

import { RegulatoryEmailDeliveryAlert } from "@/components/regulatory-documents/regulatory-email-delivery-alert";
import { RegulatoryPickupDeliveryAlert } from "@/components/regulatory-documents/regulatory-pickup-delivery-alert";
import { Button } from "@/components/ui/button/button";
import { ConfirmationDrawer } from "@/components/ui/drawer/confirmation-drawer";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page, PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import { LoadingDots } from "@/components/ui/loading/loading-dots";
import { useCallbackOnScreen } from "@/hooks/use-callback-on-screen";
import {
  useGraphQLMutationWithErrorHandler,
  useInfiniteGraphQL,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { format } from "@/lib/helpers/date";
import {
  isBindingDeclaration,
  prazoDeAssinatura,
} from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDocumentRequestsQuery = graphql(/* GraphQL */ `
  query RegulatoryDocumentRequestsQuery($first: Int!, $after: String = null) {
    solicitacoesDocumentos(first: $first, after: $after) {
      nodes {
        codSolicitacaoDocumento
        dataHora
        status
        codTipoDocumentoRegulatorio
        nomeTipoDocumento
        codMotivoDocumento
        motivo
        siglaUF
        nomeUnidade
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

const CancelarSolicitacaoDocumentoRegulatorioMutation = graphql(/* GraphQL */ `
  mutation CancelarSolicitacaoDocumentoRegulatorioMutation($codSolicitacaoDocumento: Int!) {
    cancelarSolicitacaoDocumentoRegulatorio(
      input: { codSolicitacaoDocumento: $codSolicitacaoDocumento }
    ) {
      solicitacaoDocumentoRegulatorioCanceladaOutput {
        codSolicitacaoDocumento
        status
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

type DocumentRequest = NonNullable<
  NonNullable<
    NonNullable<RegulatoryDocumentRequestsQueryQuery["solicitacoesDocumentos"]>["nodes"]
  >[number]
>;

const PAGE_SIZE = 10;

const CANCEL_DRAWER_PARAM = "cancel-document-request";

const requestsSearch = z.object({
  action: z.literal(CANCEL_DRAWER_PARAM).optional().catch(undefined),
  codSolicitacaoDocumento: z.number().optional().catch(undefined),
});

const STATUS_BADGE_COLORS: Record<string, CpsBadgeProps["color"]> = {
  Pendente: "warning",
  Entregue: "success",
  Cancelada: "danger",
};

const RequestCard = ({
  request,
  onCancel,
}: {
  request: DocumentRequest;
  onCancel: () => void;
}): JSX.Element => {
  const pending = request.status === "Pendente";
  const scopeLabel = request.nomeUnidade ? "Unidade" : "Estado";
  const scope = request.nomeUnidade ?? request.siglaUF;

  return (
    <li className="rounded-xl border border-neutral-100 bg-white p-4 shadow-xs">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-neutral-700">
          {request.nomeTipoDocumento}
        </p>
        <CpsBadge color={STATUS_BADGE_COLORS[request.status ?? ""] ?? "neutral"}>
          {request.status}
        </CpsBadge>
      </div>

      {scope ? (
        <p className="mt-2 text-xs text-neutral-500">
          {scopeLabel}: <span className="text-neutral-600">{scope}</span>
        </p>
      ) : null}

      <p className="mt-0.5 text-xs text-neutral-500">
        Motivo: <span className="text-neutral-600">{request.motivo}</span>
      </p>

      {isBindingDeclaration(request.codTipoDocumentoRegulatorio) ? (
        <p className="mt-0.5 text-xs text-neutral-500">
          Prazo de assinatura:{" "}
          <span className="text-neutral-600">
            até {prazoDeAssinatura(request.codMotivoDocumento, request.siglaUF)}
          </span>
        </p>
      ) : null}

      <p className="mt-2 text-[11px] text-neutral-400">
        Solicitado em {format(request.dataHora, "dd/MM/yyyy")}
      </p>

      {pending ? (
        <Button
          className="mt-3"
          variant="outlined"
          size="small"
          fullWidth
          onClick={onCancel}
        >
          Cancelar solicitação
        </Button>
      ) : null}
    </li>
  );
};

const RegulatoryDocumentRequestsPage = (): JSX.Element => {
  const router = useRouter();
  const search = useSearch({ from: "/settings/regulatory-documents/requests" });
  const invalidateRequests = useInvalidateQuery(RegulatoryDocumentRequestsQuery, {
    first: PAGE_SIZE,
  });

  const { mutateAsync: cancelRequest, isPending: isCanceling } =
    useGraphQLMutationWithErrorHandler(CancelarSolicitacaoDocumentoRegulatorioMutation);

  const { data, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteGraphQL(
      RegulatoryDocumentRequestsQuery,
      { first: PAGE_SIZE },
      {
        initialPageLength: PAGE_SIZE,
        getNextPageParam: (lastPage) => {
          const info = lastPage.solicitacoesDocumentos?.pageInfo;

          return info?.hasNextPage
            ? { first: PAGE_SIZE, after: info.endCursor }
            : undefined;
        },
      },
    );

  const requests = useMemo(
    () =>
      (
        data?.pages.flatMap((page) => page.solicitacoesDocumentos?.nodes ?? []) ?? []
      ).filter((request): request is DocumentRequest => request !== null),
    [data],
  );

  const ref = useCallbackOnScreen(
    () => {
      if (hasNextPage) {
        void fetchNextPage();
      }
    },
    { threshold: 1 },
  );

  const openCancelDrawer = (codSolicitacaoDocumento: number): void => {
    void router.navigate({
      to: ".",
      search: { action: CANCEL_DRAWER_PARAM, codSolicitacaoDocumento },
    });
  };

  const handleCancel = async (): Promise<void> => {
    const { codSolicitacaoDocumento } = search;

    if (codSolicitacaoDocumento === undefined) {
      return;
    }

    try {
      await cancelRequest({ codSolicitacaoDocumento });
      trackEvent("Solicitação de Documento Cancelada", { codSolicitacaoDocumento });
      invalidateRequests();
      router.history.back();
    } catch {}
  };

  const pendentes = requests.filter((request) => request.status === "Pendente");
  const hasPendingPorEmail = pendentes.some((request) => !request.nomeUnidade);
  const hasPendingParaRetirada = pendentes.some((request) =>
    Boolean(request.nomeUnidade),
  );

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Minhas solicitações" align="center" />
      </HeaderRoot>
      <Page showCustomerIoContainer={false}>
        <div className="mb-4">
          <PageTitle className="pt-0">Minhas solicitações</PageTitle>
          <PageDescription>
            Acompanhe os documentos que você pediu e o status de cada um.
          </PageDescription>
        </div>

        {isPending ? <FullPageSpinner /> : null}

        {hasPendingPorEmail ? <RegulatoryEmailDeliveryAlert /> : null}
        {hasPendingParaRetirada ? <RegulatoryPickupDeliveryAlert /> : null}

        {!isPending && requests.length === 0 ? (
          <p className="text-sm text-neutral-500">
            Você ainda não solicitou nenhum documento.
          </p>
        ) : null}

        {requests.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {requests.map((request) => (
              <RequestCard
                key={request.codSolicitacaoDocumento}
                request={request}
                onCancel={() => openCancelDrawer(request.codSolicitacaoDocumento)}
              />
            ))}
          </ul>
        ) : null}

        {hasNextPage ? (
          <div ref={ref} className="flex justify-center py-4">
            {isFetchingNextPage ? <LoadingDots /> : null}
          </div>
        ) : null}
      </Page>
      <ConfirmationDrawer
        openParam={CANCEL_DRAWER_PARAM}
        Icon={Trash}
        title="Cancelar solicitação?"
        message="Ao cancelar, este pedido não será mais assinado. Uma nova solicitação poderá ser feita a qualquer momento."
        confirmText="Cancelar solicitação"
        cancelText="Voltar"
        onConfirm={() => void handleCancel()}
        isPending={isCanceling}
      />
    </>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/requests")({
  validateSearch: requestsSearch,
  component: RegulatoryDocumentRequestsPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    if (!context.user.possuiAgendaAtiva) {
      redirect({ to: "/settings", throw: true });
    }
  },
});
